import React, { useState, useEffect } from 'react';
import { merchAPI, uploadAPI } from '../../services/api';
import '../../App.css';

function MerchAdmin() {
  const [merch, setMerch] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    image: '',
    bandcampUrl: 'https://sloomweepproductions.bandcamp.com/',
  });

  useEffect(() => {
    loadMerch();
  }, []);

  const loadMerch = async () => {
    try {
      const response = await merchAPI.getAll();
      const data = response?.data || [];
      const merchArray = Array.isArray(data) ? data : [];
      setMerch([...merchArray].reverse());
    } catch (error) {
      console.error('Error loading merch:', error);
      alert('Error loading merch');
      setMerch([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        alert('File is too large. Maximum size is 10MB.');
        e.target.value = ''; // Clear the input
        return;
      }
      
      // Check if it's an image
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file.');
        e.target.value = ''; // Clear the input
        return;
      }
      
      setImageFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);
      let imageUrl = formData.image;

      // Upload image if file is selected
      if (imageFile) {
        const uploadResponse = await uploadAPI.uploadImage(imageFile);
        imageUrl = uploadResponse.data.url;
      }

      const submitData = {
        ...formData,
        image: imageUrl,
      };

      if (editingId) {
        await merchAPI.update(editingId, submitData);
      } else {
        await merchAPI.create(submitData);
      }
      
      setFormData({ name: '', image: '', bandcampUrl: 'https://sloomweepproductions.bandcamp.com/' });
      setImageFile(null);
      setImagePreview('');
      setEditingId(null);
      loadMerch();
      alert('Merch item saved!');
    } catch (error) {
      console.error('Error saving merch:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error saving merch';
      if (errorMessage.includes('too large') || errorMessage.includes('LIMIT_FILE_SIZE')) {
        alert('File is too large. Maximum size is 10MB.');
      } else {
        alert(errorMessage);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (item) => {
    setFormData({
      name: item.name || '',
      image: item.image || '',
      bandcampUrl: item.bandcampUrl || 'https://sloomweepproductions.bandcamp.com/',
    });
    setImageFile(null);
    setImagePreview(item.image || '');
    setEditingId(item.id);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this merch item?')) {
      try {
        await merchAPI.delete(id);
        loadMerch();
      } catch (error) {
        console.error('Error deleting merch:', error);
        alert('Error deleting merch');
      }
    }
  };

  const handleMove = async (id, direction) => {
    try {
      await merchAPI.move(id, direction);
      loadMerch();
    } catch (error) {
      console.error('Error moving merch:', error);
      alert(error.response?.data?.error || 'Error moving merch');
    }
  };

  const handleCancel = () => {
    setFormData({ name: '', image: '', bandcampUrl: 'https://sloomweepproductions.bandcamp.com/' });
    setImageFile(null);
    setImagePreview('');
    setEditingId(null);
  };

  if (loading) {
    return <div className="empty-state">Loading merch...</div>;
  }

  return (
    <div>
      <h2 style={{ fontFamily: 'Bungee, cursive', marginBottom: '20px', color: '#228b22' }}>
        {editingId ? 'EDIT MERCH' : 'ADD NEW MERCH'}
      </h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Merch Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>
        <div className="form-group">
          <label>Image (optional)</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
          />
          {imagePreview && (
            <div style={{ marginTop: '10px' }}>
              <img 
                src={imagePreview} 
                alt="Preview" 
                style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
              />
            </div>
          )}
          {formData.image && !imageFile && (
            <div style={{ marginTop: '10px' }}>
              <p style={{ fontSize: '0.9rem', color: '#a0a0a0' }}>Current: {formData.image}</p>
              <img 
                src={formData.image} 
                alt="Current" 
                style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover', marginTop: '5px' }}
              />
            </div>
          )}
        </div>
        <div className="form-group">
          <label>Bandcamp URL</label>
          <input
            type="url"
            value={formData.bandcampUrl}
            onChange={(e) => setFormData({ ...formData, bandcampUrl: e.target.value })}
            required
          />
        </div>
        <button type="submit" className="btn" disabled={uploading}>
          {uploading ? 'UPLOADING...' : editingId ? 'UPDATE MERCH' : 'ADD MERCH'}
        </button>
        {editingId && (
          <button type="button" className="btn btn-secondary" onClick={handleCancel}>
            CANCEL
          </button>
        )}
        <button type="button" className="btn btn-secondary" onClick={() => setFormData({ name: '', image: '', bandcampUrl: 'https://sloomweepproductions.bandcamp.com/' })}>
          CLEAR
        </button>
      </form>

      <div className="items-list">
        {!Array.isArray(merch) || merch.length === 0 ? (
          <div className="empty-state">No merch items yet. Add one above!</div>
        ) : (
          merch.map((item) => (
            <div key={item.id} className="item-card">
              <h3>{item.name}</h3>
              {item.image && <p><strong>Image:</strong> {item.image}</p>}
              {item.bandcampUrl && <p><strong>Bandcamp URL:</strong> <a href={item.bandcampUrl} target="_blank" rel="noopener noreferrer">{item.bandcampUrl}</a></p>}
              <div className="item-actions">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => handleMove(item.id, 'up')}
                  disabled={merch.indexOf(item) === 0}
                  style={{ marginRight: '5px', minWidth: '40px' }}
                  title="Move Up"
                >
                  ↑
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => handleMove(item.id, 'down')}
                  disabled={merch.indexOf(item) === merch.length - 1}
                  style={{ marginRight: '5px', minWidth: '40px' }}
                  title="Move Down"
                >
                  ↓
                </button>
                <button className="btn btn-secondary" onClick={() => handleEdit(item)}>
                  EDIT
                </button>
                <button className="btn btn-danger" onClick={() => handleDelete(item.id)}>
                  DELETE
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default MerchAdmin;

